/**
 * ResponsivePlayground — iframe preview with Mobile / Tablet / Desktop + Light / Dark toggles
 * Inspired by https://play.tailwindcss.com/ responsive mode
 *
 * State is stored in the URL search params (shareable / survives reload):
 *   ?mode=dark&device=tablet
 *
 * Iframe notes (SSR / production):
 * - Prefer srcDoc over document.write (more reliable after hydrate)
 * - Wait for load + retry when contentDocument is briefly null
 * - Portal only after body is ready; remeasure height when content grows
 * - StyleSheetManager targets the iframe <head> so styled-components
 *   inject styles into the iframe (cloneNode cannot copy CSSOM rules)
 */

import * as React from 'react'
import { createPortal } from 'react-dom'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import queryString from 'query-string'
import styled, { StyleSheetManager } from 'styled-components'
import { ROOT_TOKEN_KEYS } from './sandbox/tokenRegistry'

type ThemeMode = 'light' | 'dark'
type Breakpoint = 'mobile' | 'tablet' | 'desktop'

export interface PlaygroundRenderProps {
  mode: ThemeMode
  breakpoint: Breakpoint
  width: number
}

const PRESETS: { id: Breakpoint; label: string; width: number }[] = [
  { id: 'mobile', label: 'Mobile', width: 402 },
  { id: 'tablet', label: 'Tablet', width: 1024 },
  { id: 'desktop', label: 'Desktop', width: 1440 },
]

const MODE_PARAM = 'mode'
const DEVICE_PARAM = 'device'

const IFRAME_SRC_DOC =
  '<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body></body></html>'

const SETUP_RETRY_MS = 50
const SETUP_RETRY_MAX = 40

const isBreakpoint = (value: unknown): value is Breakpoint =>
  value === 'mobile' || value === 'tablet' || value === 'desktop'

const isMode = (value: unknown): value is ThemeMode =>
  value === 'light' || value === 'dark'

const parseSearch = (search: string) =>
  queryString.parse(search.indexOf('?') === 0 ? search : `?${search}`)

const getModeFromSearch = (
  search: string,
  fallback: ThemeMode
): ThemeMode => {
  const mode = parseSearch(search)[MODE_PARAM]
  return isMode(mode) ? mode : fallback
}

const getDeviceFromSearch = (
  search: string,
  fallback: Breakpoint
): Breakpoint => {
  const value = parseSearch(search)[DEVICE_PARAM]
  return isBreakpoint(value) ? value : fallback
}

const Frame = styled.div`
  width: 100%;
  border-radius: 12px;
  border: 1px solid #2a2a2a;
  background-color: #0c0c0c;
  background-image: radial-gradient(circle, #2a2a2a 1px, transparent 1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  background-size: 16px 16px;
  overflow: hidden;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid #2a2a2a;
  background: #111;
`

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const ToggleGroup = styled.div`
  display: inline-flex;
  border: 1px solid #333;
  border-radius: 8px;
  overflow: hidden;
`

const ToggleButton = styled.button<{ active: boolean }>`
  appearance: none;
  border: 0;
  margin: 0;
  padding: 8px 14px;
  font: 600 12px/16px var(--font-barlow, Barlow, sans-serif);
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
  color: ${({ active }) => (active ? '#0a0a09' : '#a3a39e')};
  background: ${({ active }) => (active ? '#fff' : 'transparent')};
  transition: background 0.15s, color 0.15s;

  &:hover {
    color: ${({ active }) => (active ? '#0a0a09' : '#fff')};
  }

  & + & {
    border-left: 1px solid #333;
  }
`

const WidthTag = styled.span`
  font: 500 12px/16px var(--font-inter, Inter, sans-serif);
  color: #94a3b8;
`

const Stage = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 16px 32px;
  min-height: 200px;
  overflow-x: hidden;
`

/* Outer box uses scaled dimensions so layout matches the visual size */
const ScaleShell = styled.div<{ scale: number; width: number; height: number }>`
  width: ${({ width, scale }) => Math.round(width * scale)}px;
  height: ${({ height, scale }) => Math.round(height * scale)}px;
  flex: 0 0 auto;
  overflow: hidden;
`

const ScaleInner = styled.div<{ scale: number; width: number; height: number }>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  transform: scale(${({ scale }) => scale});
  transform-origin: top left;
`

const PreviewFrame = styled.iframe`
  display: block;
  border: 0;
  background: transparent;
  flex: 0 0 auto;
`

interface OwnProps {
  children:
    | React.ReactNode
    | ((props: PlaygroundRenderProps) => React.ReactNode)
  initialBreakpoint?: Breakpoint
  initialMode?: ThemeMode
}

type Props = OwnProps & RouteComponentProps<{}>

interface State {
  breakpoint: Breakpoint
  mode: ThemeMode
  mountNode: HTMLElement | null
  styleTarget: HTMLElement | null
  iframeHeight: number
  stageWidth: number
}

class ResponsivePlayground extends React.Component<Props, State> {
  iframeEl: HTMLIFrameElement | null = null
  stageEl: HTMLDivElement | null = null
  contentEl: HTMLElement | null = null
  styleObserver: MutationObserver | null = null
  resizeObserver: ResizeObserver | null = null
  stageResizeObserver: ResizeObserver | null = null
  syncingStyles = false
  heightFrame = 0
  setupRetries = 0
  setupRetryTimer: number | null = null
  preparedDoc: Document | null = null

  state: State = {
    breakpoint: getDeviceFromSearch(
      this.props.location.search,
      this.props.initialBreakpoint || 'mobile'
    ),
    mode: getModeFromSearch(
      this.props.location.search,
      this.props.initialMode || 'light'
    ),
    mountNode: null,
    styleTarget: null,
    iframeHeight: 120,
    stageWidth: 0,
  }

  componentDidMount() {
    this.applyIframeSize()
    // Hydrate safety: ref may have fired before the iframe document was ready
    this.trySetupIframe()
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.location.search !== this.props.location.search) {
      this.syncStateFromUrl()
    }
    if (
      prevState.breakpoint !== this.state.breakpoint ||
      prevState.iframeHeight !== this.state.iframeHeight
    ) {
      this.applyIframeSize()
    }
    if (
      prevState.breakpoint !== this.state.breakpoint ||
      prevState.mode !== this.state.mode ||
      prevState.mountNode !== this.state.mountNode
    ) {
      this.scheduleHeightUpdate()
    }
    if (prevState.mode !== this.state.mode) {
      this.applyIframeChrome()
    }
  }

  componentWillUnmount() {
    this.clearSetupRetry()
    this.detachIframeListeners()
    this.teardownObserver()
    this.teardownResizeObserver()
    this.teardownStageResizeObserver()
    if (this.heightFrame) {
      cancelAnimationFrame(this.heightFrame)
    }
  }

  syncStateFromUrl = () => {
    const { location, initialBreakpoint, initialMode } = this.props
    const mode = getModeFromSearch(location.search, initialMode || 'light')
    const breakpoint = getDeviceFromSearch(
      location.search,
      initialBreakpoint || 'mobile'
    )
    if (mode !== this.state.mode || breakpoint !== this.state.breakpoint) {
      this.setState({ mode, breakpoint })
    }
  }

  updateUrl = (updates: {
    mode?: ThemeMode
    breakpoint?: Breakpoint
  }) => {
    const { history, location } = this.props
    const params = {
      ...parseSearch(location.search),
    } as { [key: string]: string | string[] | null | undefined }

    if (updates.mode) {
      params[MODE_PARAM] = updates.mode
    }
    if (updates.breakpoint) {
      params[DEVICE_PARAM] = updates.breakpoint
    }

    const search = queryString.stringify(params)
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
    })
  }

  clearSetupRetry = () => {
    if (this.setupRetryTimer !== null) {
      window.clearTimeout(this.setupRetryTimer)
      this.setupRetryTimer = null
    }
  }

  scheduleSetupRetry = () => {
    if (this.setupRetries >= SETUP_RETRY_MAX) {
      return
    }
    this.clearSetupRetry()
    this.setupRetries += 1
    this.setupRetryTimer = window.setTimeout(
      this.handleSetupRetry,
      SETUP_RETRY_MS
    )
  }

  handleSetupRetry = () => {
    this.setupRetryTimer = null
    this.trySetupIframe()
  }

  detachIframeListeners = () => {
    if (this.iframeEl) {
      this.iframeEl.removeEventListener('load', this.handleIframeLoad)
    }
  }

  teardownObserver = () => {
    if (this.styleObserver) {
      this.styleObserver.disconnect()
      this.styleObserver = null
    }
  }

  teardownResizeObserver = () => {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  teardownStageResizeObserver = () => {
    if (this.stageResizeObserver) {
      this.stageResizeObserver.disconnect()
      this.stageResizeObserver = null
    }
  }

  observeMountNode = (node: HTMLElement | null) => {
    this.teardownResizeObserver()
    if (!node || typeof ResizeObserver === 'undefined') {
      return
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleHeightUpdate()
    })
    this.resizeObserver.observe(node)
  }

  setStageRef = (el: HTMLDivElement | null) => {
    if (el === this.stageEl) {
      return
    }
    this.teardownStageResizeObserver()
    this.stageEl = el
    if (!el) {
      return
    }
    if (typeof ResizeObserver === 'undefined') {
      this.updateStageWidth(el.clientWidth)
      return
    }
    this.stageResizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      const width = entry
        ? entry.contentRect.width
        : el.clientWidth
      this.updateStageWidth(width)
    })
    this.stageResizeObserver.observe(el)
    this.updateStageWidth(el.clientWidth)
  }

  updateStageWidth = (width: number) => {
    const next = Math.max(0, Math.floor(width))
    if (next !== this.state.stageWidth) {
      this.setState({ stageWidth: next })
    }
  }

  getPreset = () =>
    PRESETS.find((item) => item.id === this.state.breakpoint) || PRESETS[0]

  getScale = () => {
    const { stageWidth } = this.state
    const { width } = this.getPreset()
    if (!width) {
      return 1
    }
    if (!stageWidth) {
      // Avoid a full-width flash (and page overflow) before the stage is measured
      if (typeof window === 'undefined') {
        return 1
      }
      return Math.min(1, window.innerWidth / width)
    }
    return Math.min(1, stageWidth / width)
  }

  applyIframeSize = () => {
    const iframe = this.iframeEl
    if (!iframe) {
      return
    }
    const { width } = this.getPreset()
    const { iframeHeight } = this.state
    iframe.style.width = `${width}px`
    iframe.style.height = `${iframeHeight}px`
    iframe.setAttribute('width', String(width))
    iframe.setAttribute('height', String(iframeHeight))
  }

  setIframeRef = (el: HTMLIFrameElement | null) => {
    if (el === this.iframeEl) {
      if (el && !this.state.mountNode) {
        this.trySetupIframe()
      }
      return
    }

    this.clearSetupRetry()
    this.detachIframeListeners()
    this.teardownObserver()
    this.teardownResizeObserver()
    this.preparedDoc = null
    this.iframeEl = el
    this.setupRetries = 0

    if (!el) {
      this.setState({ mountNode: null, styleTarget: null })
      return
    }

    el.addEventListener('load', this.handleIframeLoad)
    this.trySetupIframe()
  }

  handleIframeLoad = () => {
    this.setupRetries = 0
    this.preparedDoc = null
    this.trySetupIframe()
  }

  trySetupIframe = () => {
    const iframe = this.iframeEl
    if (!iframe) {
      return
    }

    let doc: Document | null = null
    try {
      doc = iframe.contentDocument
    } catch (err) {
      doc = null
    }

    if (!doc || !doc.documentElement || !doc.body) {
      this.scheduleSetupRetry()
      return
    }

    this.clearSetupRetry()
    this.setupRetries = 0
    this.prepareDocument(doc)
  }

  applyIframeChrome = () => {
    const iframe = this.iframeEl
    const doc = iframe && iframe.contentDocument
    if (!doc || !doc.body) {
      return
    }
    const { mode } = this.state
    // Avoid transparent body over the dark stage when component styles lag
    doc.body.style.background =
      mode === 'dark'
        ? 'var(--neutral-darkest, #0a0a09)'
        : 'var(--neutral-white, #ffffff)'
  }

  prepareDocument = (doc: Document) => {
    const body = doc.body
    const head = doc.head
    if (!body || !head) {
      this.scheduleSetupRetry()
      return
    }

    doc.documentElement.style.height = 'auto'
    doc.documentElement.style.width = '100%'
    body.style.margin = '0'
    body.style.padding = '0'
    body.style.height = 'auto'
    body.style.overflow = 'hidden'
    body.style.width = '100%'
    body.setAttribute('data-playground-ready', 'true')
    this.applyIframeChrome()

    this.syncStyles(doc)
    this.applyIframeSize()

    const alreadyPrepared = this.preparedDoc === doc
    this.preparedDoc = doc

    if (
      this.state.mountNode !== body ||
      this.state.styleTarget !== head
    ) {
      this.setState({ mountNode: body, styleTarget: head }, () => {
        this.observeMountNode(this.contentEl)
        this.syncStyles(doc)
        this.scheduleHeightUpdate()
        window.setTimeout(this.scheduleHeightUpdate, 0)
        window.setTimeout(this.scheduleHeightUpdate, 100)
      })
    } else if (this.contentEl) {
      this.observeMountNode(this.contentEl)
      if (!alreadyPrepared) {
        this.scheduleHeightUpdate()
      }
    }

    this.teardownObserver()
    this.styleObserver = new MutationObserver(() => {
      if (this.syncingStyles) {
        return
      }
      this.syncStyles(doc)
      this.scheduleHeightUpdate()
    })
    this.styleObserver.observe(document.head, {
      childList: true,
      subtree: true,
    })
  }

  copyStyleElement = (el: HTMLElement) => {
    const clone = el.cloneNode(true) as HTMLElement
    clone.setAttribute('data-playground-sync', 'true')

    // styled-components often fills <style> via CSSOM; cloneNode won't copy those rules
    if (el.tagName === 'STYLE') {
      const styleEl = el as HTMLStyleElement
      const hasText = !!(styleEl.textContent && styleEl.textContent.trim())
      if (!hasText && styleEl.sheet) {
        try {
          const rules = Array.from(styleEl.sheet.cssRules)
            .map((rule) => rule.cssText)
            .join('\n')
          if (rules) {
            clone.textContent = rules
          }
        } catch (err) {
          // Ignore cross-origin stylesheet access errors
        }
      }
    }

    return clone
  }

  syncStyles = (doc: Document) => {
    if (!doc.head || this.syncingStyles) {
      return
    }
    this.syncingStyles = true

    try {
      const head = doc.head
      Array.from(head.querySelectorAll('[data-playground-sync]')).forEach(
        (node) => {
          if (node.parentNode) {
            node.parentNode.removeChild(node)
          }
        }
      )

      Array.from(document.head.children).forEach((node) => {
        const el = node as HTMLElement
        const isStyle = el.tagName === 'STYLE'
        const isLink =
          el.tagName === 'LINK' && (el as HTMLLinkElement).rel === 'stylesheet'
        if (!isStyle && !isLink) {
          return
        }
        // Skip empty SC sheets that StyleSheetManager will own inside the iframe
        if (isStyle) {
          const styleEl = el as HTMLStyleElement
          const hasText = !!(styleEl.textContent && styleEl.textContent.trim())
          const hasCssom =
            !!styleEl.sheet && styleEl.sheet.cssRules && styleEl.sheet.cssRules.length > 0
          if (!hasText && !hasCssom) {
            return
          }
        }
        head.appendChild(this.copyStyleElement(el))
      })

      const tokens = doc.createElement('style')
      tokens.setAttribute('data-playground-sync', 'true')
      tokens.setAttribute('data-playground-tokens', 'true')
      tokens.textContent = this.buildTokenCss()
      head.appendChild(tokens)
    } finally {
      this.syncingStyles = false
    }
  }

  buildTokenCss = () => {
    const root = document.documentElement
    const styles = window.getComputedStyle(root)
    const decls = ROOT_TOKEN_KEYS.map((key) => {
      const value = styles.getPropertyValue(key).trim()
      return value ? `${key}: ${value};` : ''
    })
      .filter(Boolean)
      .join('\n')
    return `:root {\n${decls}\n}`
  }

  scheduleHeightUpdate = () => {
    if (this.heightFrame) {
      cancelAnimationFrame(this.heightFrame)
    }
    this.heightFrame = requestAnimationFrame(() => {
      this.heightFrame = requestAnimationFrame(this.updateIframeHeight)
    })
  }

  setContentRef = (el: HTMLDivElement | null) => {
    this.contentEl = el
    this.observeMountNode(el)
    this.scheduleHeightUpdate()
  }

  updateIframeHeight = () => {
    const iframe = this.iframeEl
    const doc = iframe && iframe.contentDocument
    if (!doc || !doc.body || !doc.body.hasAttribute('data-playground-ready')) {
      return
    }
    const content = this.contentEl || (doc.body.firstElementChild as HTMLElement)
    const height = Math.max(
      content ? Math.ceil(content.getBoundingClientRect().height) : 0,
      content ? content.offsetHeight : 0,
      content ? content.scrollHeight : 0,
      80
    )
    if (height !== this.state.iframeHeight) {
      this.setState({ iframeHeight: height }, this.applyIframeSize)
    }
  }

  setBreakpoint = (breakpoint: Breakpoint) => {
    this.updateUrl({ breakpoint })
    this.setState({ breakpoint }, () => {
      this.applyIframeSize()
      this.scheduleHeightUpdate()
    })
  }

  setMode = (mode: ThemeMode) => {
    this.updateUrl({ mode })
    this.setState({ mode }, this.scheduleHeightUpdate)
  }

  renderContent = () => {
    const { children } = this.props
    const { mode, breakpoint } = this.state
    const preset = this.getPreset()
    const renderProps: PlaygroundRenderProps = {
      mode,
      breakpoint,
      width: preset.width,
    }

    if (typeof children === 'function') {
      return children(renderProps)
    }
    return children
  }

  render() {
    const { breakpoint, mode, mountNode, styleTarget, iframeHeight } = this.state
    const preset = this.getPreset()
    const scale = this.getScale()
    const widthLabel =
      scale < 1
        ? `${preset.width}px · ${Math.round(scale * 100)}%`
        : `${preset.width}px`

    return (
      <Frame>
        <Toolbar>
          <ToolbarGroup>
            <ToggleGroup role="group" aria-label="Viewport size">
              {PRESETS.map(({ id, label }) => (
                <ToggleButton
                  key={id}
                  type="button"
                  active={breakpoint === id}
                  onClick={() => this.setBreakpoint(id)}
                >
                  {label}
                </ToggleButton>
              ))}
            </ToggleGroup>
            <ToggleGroup role="group" aria-label="Color mode">
              <ToggleButton
                type="button"
                active={mode === 'light'}
                onClick={() => this.setMode('light')}
              >
                Light
              </ToggleButton>
              <ToggleButton
                type="button"
                active={mode === 'dark'}
                onClick={() => this.setMode('dark')}
              >
                Dark
              </ToggleButton>
            </ToggleGroup>
          </ToolbarGroup>
          <WidthTag>{widthLabel}</WidthTag>
        </Toolbar>
        <Stage innerRef={this.setStageRef}>
          <ScaleShell scale={scale} width={preset.width} height={iframeHeight}>
            <ScaleInner scale={scale} width={preset.width} height={iframeHeight}>
              <PreviewFrame
                innerRef={this.setIframeRef}
                title="Responsive preview"
                srcDoc={IFRAME_SRC_DOC}
              />
            </ScaleInner>
          </ScaleShell>
          {mountNode &&
            styleTarget &&
            createPortal(
              <StyleSheetManager target={styleTarget}>
                <div key={`${mode}-${breakpoint}`} ref={this.setContentRef}>
                  {this.renderContent()}
                </div>
              </StyleSheetManager>,
              mountNode
            )}
        </Stage>
      </Frame>
    )
  }
}

export default withRouter(ResponsivePlayground) as React.ComponentType<OwnProps>



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/theme/ResponsivePlayground.tsx